export interface TargetPosition {
  id: string;
  /** Viewport-relative top, i.e. `getBoundingClientRect().top`. */
  top: number;
}

export interface ScrollSpyViewport {
  /** Distance from the viewport top at which a target counts as read. */
  readingLine: number;
  /** Viewport height, so a target that is merely on screen can be recognised. */
  viewportHeight: number;
  /** The page cannot scroll further, so the lowest target is the one on screen. */
  isAtBottom: boolean;
}

/**
 * The target the reader is looking at: the last one to have crossed the reading
 * line, or the first one once it has scrolled into view. `null` while every
 * target is still below the viewport - on a page with a hero, that means the
 * hero has it to itself.
 *
 * Independent of both the targets' order and their heights. At the bottom of the
 * document the lowest target wins outright, so a short final section still
 * counts as read even though it never reaches the line.
 */
export function pickActiveTarget(
  positions: TargetPosition[],
  { readingLine, viewportHeight, isAtBottom }: ScrollSpyViewport
): string | null {
  if (positions.length === 0) return null;
  if (isAtBottom) return lowestTarget(positions).id;

  const crossed = positions.filter((position) => position.top <= readingLine);
  if (crossed.length > 0) return lowestTarget(crossed).id;

  const upcoming = highestTarget(positions);
  return upcoming.top < viewportHeight ? upcoming.id : null;
}

function lowestTarget(positions: TargetPosition[]): TargetPosition {
  return positions.reduce((lowest, position) =>
    position.top > lowest.top ? position : lowest
  );
}

function highestTarget(positions: TargetPosition[]): TargetPosition {
  return positions.reduce((highest, position) =>
    position.top < highest.top ? position : highest
  );
}
